import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';
import type { InjectedFormProps } from 'redux-form';
import { reduxForm } from 'redux-form';

import EquipmentPlaceholderItem from 'af-root/scenes/Company/WorkOrders/Order/Form/ResourceLookups/Equipment/EquipmentPlaceholderItem';

import type CreateEquipmentPlaceholderForm from 'ab-requestModels/scheduleBoardCreateEquipmentPlaceholder.requestModel';

import type { RootState } from 'af-reducers';

import { EQUIPMENT_PLACEHOLDER_FORM } from 'af-constants/reduxForms';

import { Button } from '@acceligentllc/storybook';

import CustomModal from 'af-components/CustomModal';
import Label from 'af-components/LockedValue/Label';
import SubmitButton from 'af-components/SubmitButton';

import * as ScheduleBoardActions from 'af-actions/scheduleBoard';

import { validate } from './validation';

interface OwnProps {
	resetTimer: () => void;
	onSubmit: (form: CreateEquipmentPlaceholderForm) => void;
}

type FormProps = InjectedFormProps<CreateEquipmentPlaceholderForm, FormOwnProps>;

type FormOwnProps = OwnProps & ConnectedProps<typeof connector>;
type Props = FormOwnProps & FormProps;

const EquipmentPlaceholderModal: React.FC<Props> = (props) => {
	const {
		setEquipmentPlaceholderModalVisibility,
		setEquipmentPlaceholderModalData,
		onSubmit,
		submitting,
		showModal,
		workOrderCode,
		handleSubmit,
		resetTimer,
		invalid,
	} = props;

	const closeModal = React.useCallback(() => {
		setEquipmentPlaceholderModalVisibility(false);
		setEquipmentPlaceholderModalData({ workOrderCode: null, workOrderId: null, index: null });
	}, [setEquipmentPlaceholderModalData, setEquipmentPlaceholderModalVisibility]);

	const onSave = React.useCallback(async (form: CreateEquipmentPlaceholderForm) => {
		if (onSubmit) {
			await onSubmit(form);
		}
		closeModal();
	}, [closeModal, onSubmit]);

	return (
		<CustomModal
			closeModal={closeModal}
			modalStyle="info"
			showModal={showModal}
			size="md"
		>
			<CustomModal.Header
				closeModal={closeModal}
				title={`Add Equipment Placeholder on ${workOrderCode}`}
			/>
			<CustomModal.Body>
				<Label label="Equipment Cost *" />
				<EquipmentPlaceholderItem
					isModal={true}
					name="equipmentCostId"
					propName="equipmentCost"
					resetTimer={resetTimer}
				/>
			</CustomModal.Body>
			<CustomModal.Footer>
				<Button
					label="Cancel"
					onClick={closeModal}
					style="secondary"
				/>
				<SubmitButton
					disabled={invalid}
					label="Save Placeholder"
					onClick={handleSubmit(onSave)}
					reduxFormSubmitting={submitting}
					submitKey={EQUIPMENT_PLACEHOLDER_FORM}
				/>
			</CustomModal.Footer>
		</CustomModal>
	);
};

function mapStateToProps(state: RootState) {
	const { isOpen, workOrderCode, workOrderId, index } = state.scheduleBoard.equipmentPlaceholderModal;
	return {
		showModal: isOpen,
		workOrderCode,
		index,
		workOrderId,
		initialValues: {
			index: index ?? undefined,
			workOrderId: workOrderId ?? undefined,
		},
	};
}

function mapDispatchToProps() {
	return {
		setEquipmentPlaceholderModalVisibility: ScheduleBoardActions.setEquipmentPlaceholderModalVisibility,
		setEquipmentPlaceholderModalData: ScheduleBoardActions.setEquipmentPlaceholderModalData,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentClass<OwnProps>>(
	React.memo,
	connector,
	reduxForm<CreateEquipmentPlaceholderForm, FormOwnProps>({ form: EQUIPMENT_PLACEHOLDER_FORM, enableReinitialize: true, validate })
);

export default enhance(EquipmentPlaceholderModal);
